import { useState, useEffect } from "react";
import { Plus, Info } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useCart } from "../../contexts/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatPrice(price) {
  return new Intl.NumberFormat("fr-FR").format(price) + " FCFA";
}

function ProductCard({ product, index }) {
  const { addItem } = useCart();
  const [imgError, setImgError] = useState(false);

  return (
    <div
      data-testid={`product-card-${index}`}
      className={`fade-up fade-up-delay-${(index % 3) + 1} group bg-white rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col`}
    >
      {/* Image */}
      <Link to={`/produit/${product.id}`} className="relative aspect-square bg-stone-50 overflow-hidden block" data-testid={`product-image-link-${index}`}>
        {!imgError ? (
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#6C3FC5]/10 to-[#6C3FC5]/5">
            <span className="text-[#6C3FC5] font-bold text-2xl" style={{ fontFamily: "'Playfair Display', serif" }}>
              {product.short_name}
            </span>
          </div>
        )}
        {!product.in_stock && (
          <div className="absolute inset-0 bg-stone-900/50 flex items-center justify-center">
            <span className="bg-white text-stone-900 px-4 py-2 rounded-full text-sm font-medium">
              Rupture de stock
            </span>
          </div>
        )}
        {product.discount_percentage > 0 && (
          <div className="absolute top-3 left-3 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
            -{product.discount_percentage}%
          </div>
        )}
      </Link>

      {/* Content */}
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between mb-2">
          <h3
            className="text-lg font-bold text-stone-900"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {product.name}
          </h3>
          <span className="text-xs bg-[#6C3FC5]/10 text-[#6C3FC5] px-2 py-0.5 rounded-full font-medium">
            {product.short_name}
          </span>
        </div>

        <p className="text-stone-600 text-sm mb-4 flex-1 leading-relaxed">
          {product.description}
        </p>

        <div className="mb-4">
          <span className="text-[#6C3FC5] font-semibold text-lg">
            {formatPrice(product.price)}
          </span>
          {product.discount_percentage > 0 && product.original_price > 0 && (
            <span className="ml-2 text-stone-400 text-sm line-through">
              {formatPrice(product.original_price)}
            </span>
          )}
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => addItem(product)}
            disabled={!product.in_stock}
            data-testid={`product-add-cart-${index}`}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-[#6C3FC5] hover:bg-[#582FB0] disabled:bg-stone-300 disabled:cursor-not-allowed text-white px-4 py-3 sm:py-2.5 rounded-full text-sm font-medium transition-all duration-200 hover:scale-105"
          >
            <Plus size={15} />
            Ajouter
          </button>
          <Link
            to={`/produit/${product.id}`}
            data-testid={`product-info-${index}`}
            className="inline-flex items-center justify-center w-11 h-11 sm:w-10 sm:h-10 rounded-full border-2 border-[#6C3FC5] text-[#6C3FC5] hover:bg-[#6C3FC5] hover:text-white transition-all duration-200"
            title="En savoir plus"
          >
            <Info size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function ProductGrid() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchProducts() {
      try {
        // First try to seed
        await axios.post(`${API}/seed`);
        const res = await axios.get(`${API}/products`);
        setProducts(res.data);
      } catch (err) {
        console.error("Error fetching products:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchProducts();
  }, []);

  if (loading) {
    return (
      <section id="produits" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                <div className="aspect-square bg-stone-200" />
                <div className="p-5 space-y-3">
                  <div className="h-5 bg-stone-200 rounded w-2/3" />
                  <div className="h-4 bg-stone-100 rounded w-full" />
                  <div className="h-6 bg-stone-200 rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="produits" data-testid="products-section" className="py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14 fade-up">
          <h2
            className="text-3xl md:text-4xl font-semibold text-stone-900 mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Nos Produits
          </h2>
          <p className="text-base md:text-lg text-stone-600 max-w-xl mx-auto">
            Une gamme complète de compléments alimentaires premium pour toute la famille.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product, index) => (
            <ProductCard key={product.id} product={product} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}
